/**
 * Anchor-positioned floating tooltip. Renders into a body-level portal so
 * the SVG-clipped field can host badges without the tooltip getting cut
 * off. Smooth fade + tiny lift on open; reverse on close before unmount.
 *
 * The component is controlled — parent owns `open` and the `anchorRect`
 * (from `event.currentTarget.getBoundingClientRect()`). FieldDiamond
 * uses this to bind hover/focus on each `<g>` badge.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

interface TooltipProps {
  open: boolean;
  anchorRect: DOMRect | null;
  children: React.ReactNode;
  onRequestClose: () => void;
  /** Approximate tooltip width for placement math. Defaults to 240. */
  width?: number;
}

const TRANSITION_MS = 160;

interface Pos {
  left: number;
  top: number;
  side: 'above' | 'below';
}

function computePosition(rect: DOMRect, width: number, height: number): Pos {
  const aboveTop = rect.top - height - 10;
  const belowTop = rect.bottom + 10;
  const useAbove = aboveTop > 12;
  const top = useAbove ? aboveTop : belowTop;
  const centerLeft = rect.left + rect.width / 2 - width / 2;
  const left = Math.max(8, Math.min(window.innerWidth - width - 8, centerLeft));
  return { left, top, side: useAbove ? 'above' : 'below' };
}

export default function Tooltip({
  open,
  anchorRect,
  children,
  onRequestClose,
  width = 240,
}: TooltipProps): JSX.Element | null {
  const [mounted, setMounted] = useState(open);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Pos | null>(null);

  // Open/close transition coordination.
  useEffect(() => {
    if (open) {
      setMounted(true);
      const r = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(r);
    }
    setVisible(false);
    const t = window.setTimeout(() => setMounted(false), TRANSITION_MS);
    return () => window.clearTimeout(t);
  }, [open]);

  // Position whenever the anchor or tooltip dimensions change.
  useLayoutEffect(() => {
    if (!mounted || !anchorRect) return;
    const tooltipEl = ref.current;
    const height = tooltipEl ? tooltipEl.offsetHeight : 120;
    setPos(computePosition(anchorRect, width, height));
  }, [mounted, anchorRect, width, children]);

  // ESC closes.
  useEffect(() => {
    if (!open) return undefined;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        onRequestClose();
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onRequestClose]);

  if (!mounted) return null;

  return createPortal(
    <div
      ref={ref}
      role="tooltip"
      data-testid="tooltip"
      data-open={visible ? 'true' : 'false'}
      data-side={pos?.side ?? 'above'}
      className={styles.tooltip}
      style={{
        width,
        left: pos ? `${pos.left}px` : '-9999px',
        top: pos ? `${pos.top}px` : '-9999px',
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
